import { FC, ReactNode, createContext, useContext } from 'react';

import { useTickets } from './TicketsContext';
import { GetRooftopFeaturesResponseDTO } from 'declarations/dto/features';
import { FeaturesAppsE, FeaturesNamesE } from 'declarations/enums/features';
import { useFetchRooftopFeatures } from 'hooks/api/useRooftopFeatures';

export type FeaturesContextT = {
  rootTopFeatures: GetRooftopFeaturesResponseDTO | undefined;
  isFeatureActive: (featureName: FeaturesNamesE) => boolean;
};

type FeaturesProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  rootTopFeatures: undefined,
  isFeatureActive: () => false,
};

export const FeaturesContext = createContext<FeaturesContextT>(contextDefaultValue);

const FeaturesProvider: FC<FeaturesProviderPropsT> = ({ children }) => {
  /**
   * Hooks
   */
  const { selectedRoofTop } = useTickets();

  /**
   * Api hooks
   */
  const getFeaturesResult = useFetchRooftopFeatures(selectedRoofTop);

  /**
   * Handlers
   */
  const isFeatureActive = (featureName: FeaturesNamesE): boolean => {
    return (
      !!selectedRoofTop &&
      !!getFeaturesResult.data?.some(
        ({ applicationName, featureName: name, recordAudit }) =>
          applicationName.toLowerCase() === FeaturesAppsE.Chirp &&
          name.toLowerCase() === featureName.toLowerCase() &&
          recordAudit.isActive
      )
    );
  };

  return (
    <FeaturesContext.Provider
      value={{
        rootTopFeatures: getFeaturesResult.data,
        isFeatureActive,
      }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};

const useFeatures = () => useContext(FeaturesContext);

export { FeaturesProvider, useFeatures };
